import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import StyleIcon from '@mui/icons-material/Style'
import SmallModal from '../Modals/SmallModal'
import API from '../../services/API'
import BigModal from '../Modals/BigModal'

const suggestionsCache = {}
let allTemplateSuggestions = null
let allTemplateSuggestionsRequest = null

const PresetsTextField = ({
  label,
  value,
  setValue,
  presets = [],
  suggestionPath,
  suggestionLabel = 'Suggestions',
  showInlineSuggestions = true,
  required,
  ...props
}) => {
  const [openPresets, setOpenPresets] = useState(false)
  const [templateSuggestions, setTemplateSuggestions] = useState([])

  useEffect(() => {
    if (!suggestionPath) return

    if (suggestionsCache[suggestionPath]) {
      setTemplateSuggestions(suggestionsCache[suggestionPath])
      return
    }

    if (allTemplateSuggestions) {
      const suggestions = allTemplateSuggestions[suggestionPath] ?? []
      suggestionsCache[suggestionPath] = suggestions
      setTemplateSuggestions(suggestions)
      return
    }

    if (!allTemplateSuggestionsRequest) {
      allTemplateSuggestionsRequest = API.get('/admin/emptemplate-suggestions')
    }

    allTemplateSuggestionsRequest
      .then((res) => {
        if (res.status === 200) {
          allTemplateSuggestions = res.data
          const suggestions = res.data?.[suggestionPath] ?? []
          suggestionsCache[suggestionPath] = suggestions
          setTemplateSuggestions(suggestions)
        }
      })
      .catch(() => setTemplateSuggestions([]))
  }, [suggestionPath])

  const suggestions = useMemo(() => {
    const seen = new Set()
    return [...presets, ...templateSuggestions]
      .filter((x) => typeof x === 'string' && x.trim())
      .map((x) => x.trim())
      .filter((x) => {
        const key = x.toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
  }, [presets, templateSuggestions])

  const hasSuggestions = suggestions.length > 0

  const handlePresetSelect = (preset, append = false) => {
    const nextValue =
      append && value?.trim() ? `${value.trim()}\n${preset}` : preset
    setValue(nextValue)
    setOpenPresets(false)
  }

  return (
    <>
      <TextField
        {...props}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        label={label}
        required={required}
        InputProps={{
          ...(props.InputProps ?? {}),
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                cursor: 'pointer',
              }}
            >
              {props.InputProps?.endAdornment}
            </InputAdornment>
          ),
        }}
      />
      {showInlineSuggestions && hasSuggestions && !value && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ pt: 0.4 }}>
            {suggestionLabel}:
          </Typography>
          {suggestions.slice(0, 3).map((suggestion) => (
            <Chip
              key={suggestion}
              size="small"
              label={
                suggestion.length > 58
                  ? `${suggestion.slice(0, 55).trim()}...`
                  : suggestion
              }
              onClick={() => handlePresetSelect(suggestion)}
              sx={{ maxWidth: '100%', justifyContent: 'flex-start' }}
            />
          ))}
          <Chip
            size="small"
            label="More presets"
            onClick={() => setOpenPresets(true)}
            color="contrast"
            variant="outlined"
          />
        </Stack>
      )}
      <BigModal
        autoWidth
        open={openPresets}
        onClose={() => setOpenPresets(false)}
      >
        <Stack gap={1} sx={{ maxWidth: 620 }}>
          <Typography variant="title3">{suggestionLabel}</Typography>
          {!hasSuggestions && (
            <Typography variant="body2" color="text.secondary">
              No saved suggestions are available yet.
            </Typography>
          )}
          {suggestions.map((x, index) => (
            <Box
              key={x}
              bgcolor={index % 2 ? '#F5F5F5' : '#E8E8E8'}
              padding={1}
              borderRadius={1}
            >
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', mb: 1 }}
              >
                {x}
              </Typography>
              <Stack direction="row" gap={1} justifyContent="flex-end">
                {!!value?.trim() && (
                  <Button
                    size="small"
                    onClick={() => handlePresetSelect(x, true)}
                  >
                    Append
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handlePresetSelect(x)}
                >
                  Use
                </Button>
              </Stack>
            </Box>
          ))}
        </Stack>
      </BigModal>
    </>
  )
}

PresetsTextField.propTypes = {
  label: PropTypes.string.isRequired,
  presets: PropTypes.array,
  suggestionPath: PropTypes.string,
  suggestionLabel: PropTypes.string,
  showInlineSuggestions: PropTypes.bool,
}

export default PresetsTextField
